import React, {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {getAiAgentContext} from '../../../../redux/aiAgent/aiAgentActions';
import {XHR_LOADING_STATE} from '../../../../assets/constants/constants';
import Loader from '../../../loader/Loader';
import {isIntentAvailableInScope} from '../../utils/agentSchema';
import {useFilePickerPending} from '../../hooks/useFilePickerPending';
import {
    AI_AGENT_EXTRA_SUGGESTIONS,
    AI_AGENT_SUGGESTION_DEFAULT_ICON,
    AI_AGENT_SUGGESTION_DESCRIPTION_KEYS,
    AI_AGENT_SUGGESTION_ICONS,
} from '../../constants';
import AiAgentSuggestionChip, {type AiAgentSuggestionItem} from './AiAgentSuggestionChip';
import type {TState} from '../../../../redux/store.type';
import * as css from './AiAgentSuggestions.css';

interface AiAgentSuggestionsProps {
    /** Fills the prompt box — the suggestion is a starting point, not a command. */
    onSuggestionFill: (prompt: string, index: number) => void;
    /** Same picker the prompt attach button opens — no second file input. */
    onOpenFilePicker: () => void;
    /** The explanation is shown above the prompt box, where the user is about to type. */
    onPromptHintChange: (hint: string | null) => void;
}

/** Built from the server's intent catalog — a new intent shows up with no client change. */
function AiAgentSuggestions({onSuggestionFill, onOpenFilePicker, onPromptHintChange}: AiAgentSuggestionsProps) {
    const {t, i18n} = useTranslation('aiAgent');
    // The scope comes from the pathname, so the offered actions follow the current page.
    const {pathname} = useLocation();
    const intents = useSelector((s: TState) => s.aiAgent?.meta?.intents);
    const metaPid = useSelector((s: TState) => s.aiAgent?.metaPid);
    const metaXHR = useSelector((s: TState) => s.aiAgent?.metaXHR);
    const {pending: filePickerPending, startPending} = useFilePickerPending();
    const isHebrew = i18n.language === 'he';

    const visibleSuggestions = useMemo(() => {
        const {scope} = getAiAgentContext();
        const items: Omit<AiAgentSuggestionItem, 'catalogIndex'>[] = [];
        for(const {intent, suggestion} of intents ?? []) {
            if(!suggestion || !isIntentAvailableInScope(intent, scope)) {
                continue;
            }
            const descriptionKey = AI_AGENT_SUGGESTION_DESCRIPTION_KEYS[intent];
            items.push({
                id: intent,
                label: isHebrew ? suggestion.label : suggestion.labelEn,
                prompt: isHebrew ? suggestion.prompt : suggestion.promptEn,
                description: descriptionKey ? t(descriptionKey) : null,
                Icon: AI_AGENT_SUGGESTION_ICONS[intent] ?? AI_AGENT_SUGGESTION_DEFAULT_ICON,
            });
            for(const extra of AI_AGENT_EXTRA_SUGGESTIONS) {
                if(extra.intent === intent) {
                    items.push({
                        id: `${intent}:${extra.labelKey}`,
                        label: t(extra.labelKey),
                        prompt: t(extra.promptKey),
                        description: t(extra.descriptionKey),
                        Icon: extra.Icon,
                        opensFilePicker: extra.opensFilePicker,
                    });
                }
            }
        }
        return items.map((item, catalogIndex) => ({...item, catalogIndex}));
    }, [intents, pathname, isHebrew, t]);

    // The hint belongs to the empty state — it goes when the chips go.
    useEffect(() => () => onPromptHintChange(null), [onPromptHintChange]);

    const handleSelect = useCallback((suggestion: AiAgentSuggestionItem) => {
        onSuggestionFill(suggestion.prompt, suggestion.catalogIndex);
        onPromptHintChange(suggestion.description);
        if(suggestion.opensFilePicker) {
            startPending();
            onOpenFilePicker();
        }
    }, [onOpenFilePicker, onPromptHintChange, onSuggestionFill, startPending]);

    // The loader shows only while the current package's catalog is actually on its way — a
    // failed fetch falls back to the empty state rather than spinning forever.
    const currentPid = getAiAgentContext().pid;
    if(metaXHR === XHR_LOADING_STATE && (!intents || metaPid !== currentPid)) {
        return (
            <css.suggestions data-cy="ai-agent-suggestions-loader">
                <Loader XHR={XHR_LOADING_STATE} height="auto"><></></Loader>
            </css.suggestions>
        );
    }

    return (
        <css.suggestions>
            {visibleSuggestions.map(suggestion => (
                <AiAgentSuggestionChip
                    key={suggestion.id}
                    suggestion={suggestion}
                    loading={!!suggestion.opensFilePicker && filePickerPending}
                    onSelect={handleSelect}
                />
            ))}
        </css.suggestions>
    );
}

export default AiAgentSuggestions;
